import { BookingProviderV2, useBookingV2 } from "context/BookingV2Context"
import { useEffect } from "react"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { useLang } from "hooks/useLang"
import { NextSeo } from "next-seo"
import { useEventPageQuery } from "hooks/useEventPage"
import {
    getCanonicalUrl,
    getEventPageServerSideProps,
    getLanguageAlternates,
    EventPageServerProps,
} from "utils/eventPage"
import { useWebViewPageState } from "hooks/useWebViewPageState"
import { useBookingPageInitialization } from "hooks/useEventBookingPage"
import { useClearBookingCartOnLeave } from "hooks/useClearBookingCartOnLeave"
import {
    EventRouteFullscreenError,
    EventRouteFullscreenLoader,
} from "components/EventRouteState/EventRouteState"

const BookingLayout = dynamic(() => import("components/Layout/BookingLayout"), {
    ssr: false,
})

export const getServerSideProps = async ({ req, params }) => {
    return getEventPageServerSideProps({
        req,
        params,
        requireVerifiedWebViewToken: true,
    })
}

const BookEvent = ({ appUserToken, isWebView }: EventPageServerProps) => {
    const router = useRouter()
    const { isAr, t } = useLang()
    const {
        isLoading,
        isError,
        error,
        refetch,
        eventId,
        event,
        bookingTimeSlots,
    } = useEventPageQuery()

    useEffect(() => {
        if (isError && !event && !(error instanceof Error)) {
            router.push("/404")
        }
    }, [isError, event, error, router])

    useWebViewPageState({
        isWebView,
        appUserToken,
        loadingState: isLoading && !event,
    })
    useClearBookingCartOnLeave()
    useBookingPageInitialization({
        event,
        tickets: bookingTimeSlots,
        eventId,
        isLoading,
    })

    // Show error state if fetch failed
    if (isError && !event) {
        return (
            <EventRouteFullscreenError
                title="Failed to load event data"
                description="There was a problem loading this event."
                onRetry={() => refetch()}
                onBack={() => router.push(`/events/${eventId}`)}
                backLabel="Back to Event"
                backgroundImage="/svg/v2/pass_ticket_fallback_image_light.png"
            />
        )
    }

    // Only show loading on initial data load, not during background refreshes
    // Simplified loading state: Show loader only during initial load when data isn't ready
    if (isLoading && !event) {
        return (
            <EventRouteFullscreenLoader className="h-screen w-screen bg-center bg-[rgb(57, 41, 134)]" />
        )
    }

    if (!event) {
        return null
    }
    const canonicalUrl = getCanonicalUrl(router.asPath)

    return (
        <>
            <NextSeo
                title={`${isAr ? event?.ar_title : event?.en_title} | ${t("SITE_NAME")}`}
                canonical={canonicalUrl}
                languageAlternates={getLanguageAlternates(router.asPath)}
                noindex
                nofollow
            />
            <BookingProviderV2 event={event} tickets={bookingTimeSlots}>
                <BookingContent />
            </BookingProviderV2>
        </>
    )
}

function BookingContent() {
    const { currentStep } = useBookingV2()

    return (
        <BookingLayout>
            {currentStep && <currentStep.component />}
        </BookingLayout>
    )
}

export default BookEvent
